import React, { useState } from "react";

import { Bot, Loader2, Plus, Table, Trash } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import MultipleSelector from "../../components/ui/MultipleSelector";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

import { useToast } from "../../components/hooks/use-toast";
import { useAiModel } from "../../components/hooks/useAiModel";

import RiskRatingDisplay from "../../components/Risk Matrix/RiskRatingDisplay";
import RiskMatrixSelectionModal from "../../components/Risk Matrix/RiskMatrixSelectionModal";

import functionsService from "../../services/functionsService";

const HazardCausesConsequences = ({
  project,
  riskMatrix,
  hazard,
  setHazard,
}) => {
  const [isGeneratingConsequences, setIsGeneratingConsequences] =
    useState(false);
  const [isRiskMatrixModalOpen, setIsRiskMatrixModalOpen] = useState(false);
  const [modalRiskType, setModalRiskType] = useState("unmitigated");

  const [selectedModel] = useAiModel();
  const { toast } = useToast();

  const handleChange = (name, value) => {
    setHazard((prev) => {
      // Update nested property
      if (name.includes(".")) {
        const lhsName = name.split(".")[0];
        const rhsName = name.split(".")[1];

        // Unmitigated risk
        if (lhsName === "unmitigatedRisk") {
          return {
            ...prev,
            unmitigatedRisk: {
              ...prev.unmitigatedRisk,
              [rhsName]: value, // Update the nested property
            },
          };
        }

        // Retained risk
        else if (lhsName === "retainedRisk") {
          return {
            ...prev,
            retainedRisk: {
              ...prev.retainedRisk,
              [rhsName]: value, // Update the nested property
            },
          };
        }
      }

      // Update top-level property
      return { ...prev, [name]: value };
    });
  };

  const generateConsequences = async () => {
    setIsGeneratingConsequences(true);

    try {
      const { success, consequences } =
        await functionsService.generateHazardConsequences(
          project.id,
          hazard.description,
          hazard.type,
          hazard.hazardSource,
          hazard.causes,
          selectedModel
        );

      if (!success) {
        console.error("Failed to generate consequences.");

        toast({
          variant: "destructive",
          title: "Failed to generate consequences.",
        });

        return;
      }

      // Set consequences as array
      setHazard((prev) => ({
        ...prev,
        consequences: [
          ...prev.consequences, 
          ...consequences.map((c) => c.consequenceDescription)
        ],
      }));
    } catch (error) {
      console.error("Failed to generate consequences.", error);

      toast({
        variant: "destructive",
        title: "Failed to generate consequences.",
        description: error.message,
      });
    } finally {
      setIsGeneratingConsequences(false);
    }
  };

  const handleMatrixCellSelection = (probability, severity) => {
    if (modalRiskType === "unmitigated") {
      handleChange("unmitigatedRisk.probability", probability);
      handleChange("unmitigatedRisk.severity", severity);
    } else if (modalRiskType === "retained") {
      handleChange("retainedRisk.probability", probability);
      handleChange("retainedRisk.severity", severity);
    }
  };

  const openRiskMatrixModal = (riskType) => {
    setModalRiskType(riskType);
    setIsRiskMatrixModalOpen(true);
  };

  return (
    <>
      {/* Summary Card */}
      <Card className="mb-4 bg-neutral-300 sticky top-0 z-10">
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Hazard Type</Label>

              <div className="font-medium">{hazard.type || "-"}</div>
            </div>

            <div>
              <Label>Hazard Source</Label>

              <div className="font-medium">{hazard.hazardSource || "-"}</div>
            </div>

            <div className="col-span-2">
              <Label>Description</Label>

              <div className="font-medium">{hazard.description || "-"}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Causes Section */}
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <Label>Causes</Label>

          <Button
            type="button"
            onClick={() => {
              setHazard((prev) => ({
                ...prev,
                causes: [...prev.causes, ""],
              }));
            }}
            size="sm"
          >
            <Plus size={16} className="mr-2" />
            Add Cause
          </Button>
        </div>

        {hazard.causes.map((cause, index) => (
          <div key={index} className="flex items-center space-x-2 mb-2">
            <Input
              value={cause}
              onChange={(e) => {
                const newCauses = [...hazard.causes];
                newCauses[index] = e.target.value;
                handleChange("causes", newCauses);
              }}
              placeholder="Enter cause description"
            />

            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={() => {
                const newCauses = hazard.causes.filter((_, i) => i !== index);
                handleChange("causes", newCauses);
              }}
            >
              <Trash size={16} />
            </Button>
          </div>
        ))}
      </div>

      {/* Consequences */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <Label>Consequences</Label>

          <div className="space-x-2">
            <Button
              type="button"
              size="sm"
              onClick={() => {
                setHazard((prev) => ({
                  ...prev,
                  consequences: [...prev.consequences, ""],
                }));
              }}
            >
              <Plus size={16} className="mr-2" />
              Add Consequence
            </Button>

            <Button
              type="button"
              onClick={generateConsequences}
              disabled={
                !hazard.description ||
                hazard.consequences.length > 0 ||
                isGeneratingConsequences
              }
            >
              {isGeneratingConsequences ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Bot size={16} className="mr-2" />
              )}
              Generate Consequences
            </Button>
          </div>
        </div>

        {hazard.consequences.map((consequence, index) => (
          <div key={index} className="flex items-center space-x-2 mb-2">
            <Input
              value={consequence}
              placeholder="Enter consequence description"
              onChange={(e) => {
                const updatedConsequences = [...hazard.consequence];
                updatedConsequences[index] = e.target.value;

                handleChange("consequences", updatedConsequences);
              }}
            />

            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={() => {
                const updatedConsequences = hazard.consequences.filter(
                  (_, i) => i !== index
                );
                handleChange("consequences", updatedConsequences);
              }}
            >
              <Trash size={16} />
            </Button>
          </div>
        ))}
      </div>

      {/* Users multi-select */}

      <div>
        <Label>Affected User(s)</Label>

        <MultipleSelector
          hidePlaceholderWhenSelected
          defaultOptions={
            project.projectMetadata?.users.map((user) => {
              return { label: user, value: user };
            }) || []
          }
          value={
            hazard.users.map((user) => {
              return { label: user, value: user };
            }) || []
          }
          onChange={(value) =>
            handleChange(
              "users",
              value.map((x) => x.value)
            )
          }
          placeholder="Select affected user(s)..."
        />
      </div>

      {/* Unmitigated Risk */}
      <div>
        <Label className="block text-md font-semibold">Unmitigated Risk</Label>

        <div className="flex space-x-4">
          <div className="w-1/2">
            <Label htmlFor="unmitigatedRiskProbability">Probability</Label>

            <Select
              value={hazard.unmitigatedRisk.probability}
              onValueChange={(value) =>
                handleChange("unmitigatedRisk.probability", value)
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select probability" />
              </SelectTrigger>

              <SelectContent className="z-[10001]">
                {riskMatrix?.probabilities?.map((prob) => (
                  <SelectItem key={prob} value={prob}>
                    {prob}
                  </SelectItem>
                )) || []}
              </SelectContent>
            </Select>
          </div>

          <div className="w-1/2">
            <Label htmlFor="unmitigatedRiskSeverity">Severity</Label>

            <Select
              value={hazard.unmitigatedRisk.severity}
              onValueChange={(value) =>
                handleChange("unmitigatedRisk.severity", value)
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select severity" />
              </SelectTrigger>

              <SelectContent className="z-[10001]">
                {riskMatrix?.severities?.map((sev) => (
                  <SelectItem key={sev} value={sev}>
                    {sev}
                  </SelectItem>
                )) || []}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-between items-center mt-2">
          <RiskRatingDisplay
            probability={hazard.unmitigatedRisk.probability}
            severity={hazard.unmitigatedRisk.severity}
            riskMatrix={riskMatrix}
          />

          <Button
            variant="outline"
            size="sm"
            onClick={() => openRiskMatrixModal("unmitigated")}
          >
            <Table className="h-4 w-4 mr-2" />
            Select from Matrix
          </Button>
        </div>
      </div>

      {/* Risk Matrix Selection Modal */}
      <RiskMatrixSelectionModal
        isOpen={isRiskMatrixModalOpen}
        setIsOpen={setIsRiskMatrixModalOpen}
        riskMatrix={riskMatrix}
        onSelectCell={handleMatrixCellSelection}
        title={`Select ${modalRiskType === "unmitigated" ? "Unmitigated" : "Retained"} Risk Values`}
        description="Click on a cell in the risk matrix to select both probability and severity values."
        riskType={modalRiskType}
      />
    </>
  );
};

export default HazardCausesConsequences;
